import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { Box, HStack, NativeSelect, Separator, Spinner, Stack } from "@chakra-ui/react"
import { Button, Text } from "@/components"
import { getActivityFeed, getActivityActors, type ActivityFeedItem } from "@/api/activity"

const formatLogDate = (dateString: string) => {
  const d = new Date(dateString)
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const typeColor = (type: ActivityFeedItem["type"]) => {
  switch (type) {
    case "deal": return "blue.500"
    case "company": return "green.500"
    case "hotlist": return "orange.500"
    case "fmv": return "purple.500"
  }
}

const typeLabel = (type: ActivityFeedItem["type"]) => {
  switch (type) {
    case "deal": return "Deal"
    case "company": return "Company"
    case "hotlist": return "Hotlist"
    case "fmv": return "FMV"
  }
}

export const ActivityComponent = () => {
  const navigate = useNavigate()
  const [selectedPerson, setSelectedPerson] = useState<string>("all")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: actors = [] } = useQuery({
    queryKey: ["activityActors"],
    queryFn: getActivityActors,
    staleTime: 1000 * 60 * 10,
  })

  const personId = selectedPerson !== "all" ? Number(selectedPerson) : undefined

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["activityFeed", personId],
    queryFn: ({ pageParam }) =>
      getActivityFeed({
        esoPersonId: personId,
        limit: 50,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || lastPage.items.length === 0) return undefined
      return lastPage.items[lastPage.items.length - 1].createdAt
    },
    staleTime: 1000 * 60 * 2,
  })

  const allItems = useMemo(() => {
    const items = data?.pages.flatMap((p) => p.items) ?? []
    if (typeFilter === "all") return items
    return items.filter((item) => item.type === typeFilter)
  }, [data, typeFilter])

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!bottomRef.current || isFetchingNextPage || !hasNextPage) return
    const rect = bottomRef.current.getBoundingClientRect()
    if (rect.top < window.innerHeight + 200) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true)
    return () => window.removeEventListener("scroll", handleScroll, true)
  }, [handleScroll])

  const handleItemClick = (item: ActivityFeedItem) => {
    if (item.dealId) {
      navigate(`/core/deal/${item.dealId}`)
    } else if (item.companyId) {
      navigate(`/core/company/${item.companyId}`)
    }
  }

  return (
    <Box p={6} maxW="900px" mx="auto">
      <Text fontSize="2xl" fontWeight="bold" mb={1}>Activity Log</Text>
      <Text fontSize="sm" color="text.muted" mb={5}>
        All changes across deals, companies, hotlist, and FMV reviews.
      </Text>

      {/* Filters */}
      <HStack gap={3} mb={5} flexWrap="wrap">
        <NativeSelect.Root size="sm" w="220px">
          <NativeSelect.Field
            value={selectedPerson}
            onChange={(e) => setSelectedPerson(e.target.value)}
          >
            <option value="all">All People</option>
            {actors.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.shortName || a.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        <NativeSelect.Root size="sm" w="160px">
          <NativeSelect.Field
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="deal">Deal</option>
            <option value="company">Company</option>
            <option value="hotlist">Hotlist</option>
            <option value="fmv">FMV</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </HStack>

      {/* Feed */}
      {isLoading ? (
        <HStack justify="center" py={10}>
          <Spinner size="lg" />
        </HStack>
      ) : allItems.length === 0 ? (
        <Text color="text.muted" py={10} textAlign="center">
          No activity found.
        </Text>
      ) : (
        <Stack gap={0}>
          {allItems.map((item, i) => (
            <Stack key={item.id} gap={1} py={3}>
              <HStack gap={2} flexWrap="wrap">
                <Text
                  fontSize="2xs"
                  fontWeight="bold"
                  color={typeColor(item.type)}
                  textTransform="uppercase"
                >
                  {typeLabel(item.type)}
                </Text>
                {item.companyName && (
                  <Text
                    fontSize="2xs"
                    fontWeight="semibold"
                    color="text.muted"
                    cursor="pointer"
                    _hover={{ textDecoration: "underline" }}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (item.companyId) navigate(`/core/company/${item.companyId}`)
                    }}
                  >
                    {item.companyName}
                  </Text>
                )}
                <Text fontSize="2xs" color="text.muted">
                  {formatLogDate(item.createdAt)}
                </Text>
                {item.actorName && (
                  <Text fontSize="2xs" color="text.muted">
                    by {item.actorName}
                  </Text>
                )}
              </HStack>
              <Text
                fontSize="sm"
                cursor={item.dealId || item.companyId ? "pointer" : undefined}
                _hover={item.dealId || item.companyId ? { color: "blue.500" } : undefined}
                onClick={() => handleItemClick(item)}
              >
                {item.description}
              </Text>
              {i < allItems.length - 1 && <Separator mt={2} />}
            </Stack>
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={bottomRef} />

          {isFetchingNextPage && (
            <HStack justify="center" py={4}>
              <Spinner size="sm" />
              <Text fontSize="sm" color="text.muted">Loading more...</Text>
            </HStack>
          )}

          {hasNextPage && !isFetchingNextPage && (
            <HStack justify="center" py={4}>
              <Button variant="ghost" size="sm" onClick={() => fetchNextPage()}>
                Load more
              </Button>
            </HStack>
          )}
        </Stack>
      )}
    </Box>
  )
}
